import { COLORS, TEXT_STYLES, TYPOGRAPHY, VARIANTS_STYLES } from '@dehaat/dds'
import React, { useState } from 'react'
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ViewStyle,
  StyleProp,
} from 'react-native'

interface Props {
  date: number | null
  onDateSelect: (selectedDate: string) => void
  style?: StyleProp<ViewStyle>
  pastDateDisabled?: boolean
  futureDateDisabled?: boolean
}

const Calender = ({
  date,
  onDateSelect,
  style,
  pastDateDisabled,
  futureDateDisabled,
}: Props) => {
  const currentDate = new Date()
  const selectedFullDate = date ? new Date(date) : currentDate

  const [selectedMonth, setSelectedMonth] = useState<number>(
    selectedFullDate.getMonth(),
  )
  const [selectedYear, setSelectedYear] = useState<number>(
    selectedFullDate.getFullYear(),
  )

  const selectedDate = selectedFullDate.getDate()

  const daysInMonth = (month: number, year: number) =>
    new Date(year, month + 1, 0).getDate()

  const isPastDate = (date: number) => {
    const testDate = new Date(selectedYear, selectedMonth, date).setHours(
      0,
      0,
      0,
      0,
    )
    const today = currentDate.setHours(0, 0, 0, 0)
    return testDate < today
  }
  const isFutureDate = (date: number) => {
    const testDate = new Date(selectedYear, selectedMonth, date).setHours(
      0,
      0,
      0,
      0,
    )
    const today = currentDate.setHours(0, 0, 0, 0)
    return testDate > today
  }

  const incrementMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0)
      setSelectedYear(prev => prev + 1)
    } else {
      setSelectedMonth(prev => prev + 1)
    }
  }

  const decrementMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11)
      setSelectedYear(prev => prev - 1)
    } else {
      setSelectedMonth(prev => prev - 1)
    }
  }

  const handleDateSelect = (date: number) => {
    if (pastDateDisabled && isPastDate(date)) return
    if (futureDateDisabled && isFutureDate(date)) return

    const finalDate = new Date(selectedYear, selectedMonth, date)
    const formattedDate = `${finalDate.getFullYear()}-${String(
      finalDate.getMonth() + 1,
    ).padStart(2, '0')}-${String(finalDate.getDate()).padStart(2, '0')}`
    onDateSelect(formattedDate)
  }

  return (
    <View style={[styles.container, style]}>
      <View style={styles.controls}>
        <Pressable onPress={decrementMonth} style={styles.cellBtn}>
          <Text>{'<'}</Text>
        </Pressable>
        <View>
          <Text
            style={[
              TYPOGRAPHY.HEADING3,
              VARIANTS_STYLES.MEDIUM,
              TEXT_STYLES.textPrimary100,
            ]}
          >
            {`${selectedDate} ${new Date(
              selectedYear,
              selectedMonth,
            ).toLocaleString('default', {
              month: 'long',
            })} ${selectedYear}`}
          </Text>
        </View>

        <Pressable onPress={incrementMonth} style={styles.cellBtn}>
          <Text>{'>'}</Text>
        </Pressable>
      </View>

      <View style={styles.calendarGrid}>
        {Array.from({ length: daysInMonth(selectedMonth, selectedYear) }).map(
          (_, index) => {
            const date = index + 1
            const isDisabled =
              (pastDateDisabled && isPastDate(date)) ||
              (futureDateDisabled && isFutureDate(date))
            return (
              <Pressable
                key={`date_${date}`}
                style={[
                  styles.cellBtn,
                  selectedDate === date && styles.selectedDate,
                ]}
                onPress={() => !isDisabled && handleDateSelect(date)}
                disabled={isDisabled}
              >
                <Text
                  style={[
                    TYPOGRAPHY.TEXT2,
                    VARIANTS_STYLES.MEDIUM,
                    TEXT_STYLES.textNeutral100,
                    selectedDate === date && TEXT_STYLES.textPrimary100,
                    isDisabled && TEXT_STYLES.textNeutral40,
                  ]}
                >
                  {date}
                </Text>
              </Pressable>
            )
          },
        )}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: COLORS.neutral00,
    width: 356,
    height: 316,
    padding: 16,
    borderRadius: 16,
  },

  controls: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },

  calendarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },

  cellBtn: {
    borderRadius: 18,
    borderWidth: 1,
    borderColor: COLORS.neutral10,
    height: 36,
    width: 36,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLORS.white,
  },
  selectedDate: {
    backgroundColor: COLORS.primary10,
    borderColor: COLORS.primary100,
  },
})

export default Calender
